/**
 * Auth API - login, logout, session management, OAuth.
 * Base URL: set VITE_API_BASE_URL in .env
 *
 * Endpoints:
 *   POST /bot/api/auth/login             → login with email & password
 *   POST /bot/api/auth/logout            → logout and clear session
 *   GET  /bot/api/auth/session           → verify session / get current user
 *   GET  /bot/api/auth/google            → initiate Google OAuth flow
 *   GET  /bot/api/auth/google/callback   → Google OAuth callback
 *   Shopify OAuth: constructed client-side → Shopify admin authorize URL
 *
 * JWT token (24h TTL) is stored in localStorage alongside its expiry
 * timestamp. All outgoing requests include Authorization: Bearer <token>.
 *
 * User object shape:
 *   { id, name, email, role, avatar, company_id }
 */

import { getBaseUrl, getShopifyEmbeddedAppHandle } from './utils';

const TOKEN_KEY = 'aerochat_token';
const TOKEN_EXPIRY_KEY = 'aerochat_token_expiry';
const USER_KEY = 'aerochat_user';

const TOKEN_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const TOKEN_REFRESH_BUFFER_MS = 30 * 60 * 1000; // treat as expired 30 min early
const LARAVEL_ADMIN_URL = import.meta.env.VITE_LARAVEL_ADMIN_URL || 'https://app.aerochat.ai';

// ---- Token helpers ----

export function getToken() {
  if (isTokenExpired()) {
    clearAuth();
    return null;
  }
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + TOKEN_TTL_MS));
}

function isTokenExpired() {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return !localStorage.getItem(TOKEN_KEY);
  return Date.now() >= Number(expiry) - TOKEN_REFRESH_BUFFER_MS;
}

export function getTokenExpiresIn() {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return 0;
  return Math.max(0, Number(expiry) - Date.now());
}

function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(USER_KEY);
}

export function removeToken() {
  clearAuth();
}

export function getStoredUser() {
  try {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
}

/**
 * If the login response includes a redirect_url (Laravel companies),
 * redirect the browser there. Returns true if redirecting.
 *
 * Backward-compatible: if use_react_admin is undefined (old backend),
 * we treat it as React and do NOT redirect.
 */
export function handleLaravelRedirect(responseData) {
  if (responseData?.redirect_url) {
    window.location.href = responseData.redirect_url;
    return true;
  }
  // Only redirect when explicitly false — undefined means old backend (stay on React)
  if (responseData?.user && responseData.user.use_react_admin === false) {
    window.location.href = `${LARAVEL_ADMIN_URL}/login`;
    return true;
  }
  return false;
}

export function isAuthenticated() {
  return !!getToken();
}

// ---- API helpers ----

async function parseJson(res) {
  const data = await res.json();
  if (!res.ok) {
    const message = data?.message || data?.error || res.statusText || 'Request failed';
    // Preserve the raw body + status so callers can branch on machine-readable
    // fields (e.g. AppSumo's `reason: "account_exists"`). Backward compatible —
    // existing callers only read err.message.
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  if (data.status === 'success' && data.data) {
    return data.data;
  }
  return data.data != null ? data.data : data;
}

/**
 * Get auth headers for API calls.
 * Use this in all other API files:
 *   import { getAuthHeaders } from '../api/auth';
 *   fetch(url, { headers: { ...getAuthHeaders() } })
 */
export function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---- OAuth helpers ----

/**
 * Get Google OAuth login URL.
 * Redirects user to backend which handles the Google OAuth flow.
 * GET /bot/api/auth/google → redirects to Google consent screen
 */
export function getGoogleLoginUrl({ plan, cycle } = {}) {
  const base = getBaseUrl();
  const params = new URLSearchParams();
  if (plan) params.set('plan', plan);
  if (cycle) params.set('cycle', cycle);
  const qs = params.toString();
  return `${base}/bot/api/auth/google${qs ? `?${qs}` : ''}`;
}

/**
 * Build the Shopify admin deep link for the embedded app.
 *
 * This used to point at the standalone middleware app's `/install?shop=`
 * route, which ran the legacy authorization-code-grant OAuth flow. Two
 * reasons that is wrong now:
 *
 *   1. The embedded app uses Shopify managed installation, so there is no
 *      app-side install route to redirect to — Shopify installs the app and
 *      grants scopes without ever calling us.
 *   2. That route belongs to a DIFFERENT Shopify app (handle "aerochatapp",
 *      the aerochat-v1 middleware) with its own client_id. Sending merchants
 *      there installed the old app, which is how accounts ended up in the
 *      legacy state the embedded app then has to repair.
 *
 * The embedded app also has no login screen of its own — opening it inside
 * Shopify admin IS the session — so "Continue with Shopify" can only hand the
 * merchant back to their admin.
 *
 * @param {string} storeName - e.g. "ikea-aerochat" (without .myshopify.com)
 * @returns {string|null} Admin deep link, or null when the app handle is unset
 */
export function buildShopifyAdminAppUrl(storeName) {
  const handle = getShopifyEmbeddedAppHandle();
  if (!handle) return null;
  return `https://admin.shopify.com/store/${encodeURIComponent(storeName)}/apps/${handle}`;
}

/**
 * Handle OAuth callback - extract token from URL params after redirect.
 * Call this on app mount to check if user just completed OAuth login.
 * @returns {object|null} user object if OAuth callback detected, null otherwise
 */
export function handleOAuthCallback() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (!token) return null;

  setToken(token);

  const userParam = params.get('user');
  let user = null;
  if (userParam) {
    try {
      user = JSON.parse(decodeURIComponent(userParam));
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {
      console.warn('Failed to parse user from OAuth callback');
    }
  }

  // Clean URL
  window.history.replaceState({}, '', window.location.pathname);
  return user;
}

/**
 * Register a new account.
 * POST /bot/api/auth/register
 *
 * Expected response:
 * {
 *   "status": "success",
 *   "data": {
 *     "user_id": 123,
 *     "company_id": 456,
 *     "secretkey": "abc123",
 *     "message": "Account created successfully"
 *   }
 * }
 */
export async function register({ firstName, lastName, email, password, planId = 'free', planType = 'monthly', ref = null, promoCode = null }) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/auth/register`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      plan_id: planId,
      plan_type: planType,
      // Partner/referral attribution — only sent when present.
      ...(ref ? { ref } : {}),
      ...(promoCode ? { promo_code: promoCode } : {}),
    }),
  });
  return parseJson(res);
}

/**
 * Exchange a conversation-access token (from an escalation / ticket-transfer
 * email deep-link, `/autologin?act=...`) for a fresh session token.
 * POST /bot/api/auth/conversation-access
 * @returns {Promise<string>} a session JWT
 */
export async function redeemConversationAccess(accessToken) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/auth/conversation-access`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token: accessToken }),
  });
  if (!res.ok) {
    throw new Error('This link is invalid or has expired.');
  }
  const data = await parseJson(res);
  const token = data?.data?.token || data?.token;
  if (!token) throw new Error('This link is invalid or has expired.');
  return token;
}

/**
 * Redeem an AppSumo license to create a new AeroChat account.
 * POST /auth/appsumo/register
 *
 * Policy: NEW USERS ONLY. The endpoint rejects emails already in
 * the users table with a 409 — the UI surfaces that as a friendly
 * "this license can only be redeemed by new users" message.
 *
 * Expected success response:
 * {
 *   "status": "success",
 *   "data": { "user_id", "company_id", "secretkey", "plan_id", "tier" }
 * }
 */
export async function registerAppSumo({ licenseKey, email, password, firstName, lastName }) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/auth/appsumo/register`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      license_key: licenseKey,
      email,
      password,
      first_name: firstName,
      last_name: lastName,
    }),
  });
  return parseJson(res);
}

/**
 * Attach an AppSumo license to the CURRENT logged-in user's account.
 * POST /bot/api/auth/appsumo/attach-license  (requires Authorization: Bearer)
 *
 * Allowed only for users on the free/trial plan, or legacy users with no
 * subscription yet. Paid or already-AppSumo accounts are rejected with a
 * friendly error the caller can surface verbatim.
 *
 * Expected success response:
 * { "status": "success", "data": { "company_id", "plan_id", "tier" } }
 */
export async function attachAppSumoLicense({ licenseKey }) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/auth/appsumo/attach-license`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ license_key: licenseKey }),
  });
  return parseJson(res);
}

/**
 * Public, PII-free status for an AppSumo license key.
 * GET /bot/api/auth/appsumo/license-status?license_key=...
 * Returns { known, claimed, active, tier } (or { known:false }).
 */
export async function getAppSumoLicenseStatus(licenseKey) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/auth/appsumo/license-status?license_key=${encodeURIComponent(licenseKey)}`
    .replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  return parseJson(res);
}

/**
 * Login with email and password.
 * POST /bot/api/auth/login
 *
 * Expected response:
 * {
 *   "status": "success",
 *   "data": {
 *     "token": "eyJhbGciOiJIUzI1NiIs...",
 *     "user": { "id", "name", "email", "role", "avatar", "company_id" }
 *   }
 * }
 */
export async function login(email, password) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/auth/login`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });
  const data = await parseJson(res);

  if (data.token) {
    setToken(data.token);
    // Handle both { token, user: {...} } and flat shapes where user fields are on data itself
    const user = data.user || data;
    if (user && user !== data.token) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      if (!user.company_id) {
        console.warn('Login response missing company_id in user object:', user);
      }
    }
  }

  return data;
}

/**
 * Request a password reset link.
 * POST /bot/api/auth/forgot-password
 *
 * @param {string} email
 * @returns {Promise<object>}
 */
export async function forgotPassword(email) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/auth/forgot-password`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ email }),
  });
  return parseJson(res);
}

/**
 * Auto-login with just an email (no password).
 * POST /bot/api/auth/auto-login
 *
 * Expected response: same shape as login
 * {
 *   "status": "success",
 *   "data": {
 *     "token": "eyJhbGciOiJIUzI1NiIs...",
 *     "user": { "id", "name", "email", "role", "avatar", "company_id" }
 *   }
 * }
 */
export async function autoLogin(email) {
  const base = getBaseUrl();
  const url = `${base}/bot/api/auth/auto-login`.replace(/([^:]\/)\/+/g, '$1');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ email }),
  });
  const data = await parseJson(res);

  if (data.token) {
    setToken(data.token);
    const user = data.user || data;
    if (user && user !== data.token) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  }

  return data;
}

/**
 * Logout - clear session.
 * POST /bot/api/auth/logout
 */
export async function logout() {
  try {
    const base = getBaseUrl();
    const url = `${base}/bot/api/auth/logout`.replace(/([^:]\/)\/+/g, '$1');
    await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
    });
  } catch (err) {
    console.warn('Logout API failed:', err);
  } finally {
    removeToken();
  }
}

/**
 * Verify current session / refresh user data.
 * GET /bot/api/auth/session
 *
 * Expected response:
 * {
 *   "status": "success",
 *   "data": {
 *     "user": { "id", "name", "email", "role", "avatar", "company_id" }
 *   }
 * }
 *
 * @returns {Promise<object|null>} user object or null if not authenticated
 */
export async function verifySession() {
  try {
    const token = getToken();
    if (!token) return null;

    const base = getBaseUrl();
    const url = `${base}/bot/api/auth/session`.replace(/([^:]\/)\/+/g, '$1');
    const res = await fetch(`${url}?_t=${Date.now()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (res.status === 401 || res.status === 403) {
      removeToken();
      return null;
    }

    if (!res.ok) {
      // Server error / endpoint not ready — keep the stored session alive
      return getStoredUser();
    }

    const data = await parseJson(res);
    const freshUser = data.user || data;
    // Merge with stored user so we don't lose fields (e.g. company_id) if the
    // session endpoint returns a partial object or omits some fields
    const storedUser = getStoredUser();
    // Keep stored company_id if session response doesn't include one
    const user = storedUser
      ? { ...storedUser, ...freshUser, company_id: freshUser.company_id || storedUser.company_id }
      : freshUser;
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return user;
  } catch (err) {
    // Network error, CORS, server down — don't destroy the session
    console.warn('Session verification failed (keeping stored session):', err);
    return getStoredUser();
  }
}